import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { NAV } from "@/constants/testIds/tickets";
import NotificationBell from "@/components/NotificationBell";
import { House, Ticket, EnvelopeOpen, BookOpen, GearSix, SignOut, Command, ChartLineUp, ClipboardText, Calendar, UsersThree, UserPlus, UserMinus, LinkSimple } from "@phosphor-icons/react";
import { Toaster } from "sonner";
import { initials } from "@/lib/format";

// Two sidebar items point at `/tickets` with different `?teams=` values.
// `NavLink`'s default isActive matches pathname only, which would light
// both up whenever the user is on any /tickets URL. This helper compares
// pathname + the `teams` query param so exactly one is highlighted.
function useTicketsNavActive(to) {
  const loc = useLocation();
  const [path, query = ""] = to.split("?");
  if (loc.pathname !== path) return false;
  const target = new URLSearchParams(query).get("teams");
  if (!target) return !loc.search.includes("teams=");
  return new URLSearchParams(loc.search).get("teams") === target;
}

const Item = ({ to, icon: Icon, label, testId }) => {
  // For /tickets links carrying a `teams` scope, override NavLink's
  // pathname-only isActive so BSys and IT don't light up together.
  const isTicketsScoped = to.startsWith("/tickets?");
  const scopedActive = useTicketsNavActive(to);
  return (
    <NavLink
      to={to}
      data-testid={testId}
      end={isTicketsScoped}
      className={({ isActive }) => {
        const active = isTicketsScoped ? scopedActive : isActive;
        return `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors duration-100 ${
          active
            ? "bg-[#39A36E]/10 text-[#39A36E] font-medium"
            : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
        }`;
      }}
    >
      <Icon size={18} weight="duotone" />
      <span>{label}</span>
    </NavLink>
  );
};

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";
  const isStaff = user?.role === "admin" || user?.role === "agent";

  return (
    <div className="grain min-h-screen flex text-slate-900" style={{ background: "#FFFFFF" }}>
      <aside className="w-[240px] shrink-0 border-r border-slate-200 flex flex-col relative z-10 sticky top-0 self-start h-screen">
        <div className="px-4 py-4 flex items-center gap-2 border-b border-slate-200 shrink-0">
          <div className="w-8 h-8 rounded-md bg-gradient-to-br from-[#263E4F] to-[#39A36E] flex items-center justify-center font-heading font-bold text-white">
            A
          </div>
          <div>
            <div className="font-heading font-bold text-[15px] leading-none text-[#263E4F]">Business Systems Tickets</div>
            <div className="text-[11px] text-slate-500 font-mono mt-1">Aurora Energy Services</div>
          </div>
        </div>

        <nav className="p-2 flex flex-col gap-0.5">
          <Item to="/" icon={House} label="Dashboard" testId={NAV.sidebarDashboard} />
          <Item to="/tickets?teams=systems" icon={Ticket} label="Bsys Tickets" testId={NAV.sidebarTickets} />
          <Item to="/tickets?teams=it" icon={Ticket} label="IT Tickets" testId="nav-it-tickets" />
          <Item to="/insights" icon={ChartLineUp} label="Insights" testId="nav-insights" />
          <Item to="/kb" icon={BookOpen} label="Knowledge base" testId={NAV.sidebarKb} />
          {isStaff && <Item to="/admin/forms" icon={ClipboardText} label="Forms" testId="nav-forms" />}
          {isStaff && <Item to="/team" icon={UsersThree} label="Team" testId="nav-team" />}
          <Item to="/admin/schedule" icon={Calendar} label="Schedule" testId="nav-schedule" />
          {isStaff && <Item to="/new-starts" icon={UserPlus} label="New Starts" testId="nav-new-starts" />}
          {isStaff && <Item to="/offboards" icon={UserMinus} label="Offboards" testId="nav-offboards" />}
          {isAdmin && <Item to="/admin/email-log" icon={EnvelopeOpen} label="Email log" testId="nav-email-log" />}
          {isAdmin && <Item to="/admin/threading-log" icon={LinkSimple} label="Threading log" testId="nav-threading-log" />}
          <Item to="/settings" icon={GearSix} label="Settings" testId={NAV.sidebarSettings} />
        </nav>

        <div className="mt-auto p-3 border-t border-slate-200">
          <div
            data-testid={NAV.userMenu}
            className="flex items-center gap-2 p-2 rounded-md hover:bg-slate-100 transition-colors cursor-pointer"
            onClick={() => navigate("/settings")}
          >
            <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-xs font-medium">
              {user?.avatar_url ? (
                <img src={user.avatar_url} alt="" className="w-full h-full rounded-full object-cover" />
              ) : (
                initials(user?.name)
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm truncate">{user?.name}</div>
              <div className="text-[11px] text-slate-500 truncate">{user?.role}</div>
            </div>
            <button
              onClick={(e) => { e.stopPropagation(); logout(); }}
              data-testid={NAV.logoutButton}
              className="p-1.5 rounded hover:bg-slate-200 text-slate-600 hover:text-slate-900"
              title="Sign out"
            >
              <SignOut size={16} />
            </button>
          </div>
        </div>
      </aside>

      <main className="flex-1 min-w-0 relative z-10">
        <div className="sticky top-0 z-20 backdrop-blur-xl bg-[#FFFFFF]/70 border-b border-slate-200">
          <div className="flex items-center gap-3 px-6 h-14">
            <div className="flex items-center gap-2 text-xs text-slate-500 font-mono">
              <Command size={14} />
              <span>bsys / dashboard</span>
            </div>
            <div className="ml-auto flex items-center gap-3 text-xs text-slate-500">
              <NotificationBell />
              <span className="font-mono">{new Date().toLocaleDateString()}</span>
            </div>
          </div>
        </div>
        <div className="p-6 fade-in">{children}</div>
      </main>
      <Toaster theme="light" position="bottom-right" />
    </div>
  );
}
